import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  getEsStatus,
  startReindex,
  startReindexAll,
  getReindexJobs,
  cancelReindexJob,
  deleteEsIndex,
  clearCache,
  getDbStats,
  getIndexMapping,
  getServicesHealth,
  getS3Backups,
  listN8nWorkflows,
  activateN8nWorkflow,
  deactivateN8nWorkflow,
  listN8nExecutions,
  stopN8nExecution,
  deleteN8nExecution,
  stopAllN8nExecutionsForWorkflow,
  getCareerSweepStatus,
  restartCareerSweep,
  type CareerSweepStatus,
  type EsStatusResponse,
  type DbStatsResponse,
  type ReindexJob,
  type EsIndexMappingResponse,
  type ServicesHealthResponse,
  type S3BackupsResponse,
  type N8nWorkflow,
  type N8nExecution,
} from "@/services/adminApi";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RefreshCw, Database, Search, Trash2, Zap, X, CheckCircle, XCircle, Clock, Loader2, HeartPulse, Workflow, Play, Pause, StopCircle, HardDrive, AlertTriangle, TrendingUp } from "lucide-react";
import { confirmDialog } from "@/components/ui/confirm-dialog";
import {
  CAREER_SWEEP_POLL_MS,
  careerSweepBadge,
  formatCoverage,
  isCareerSweepActive,
  toPercent,
} from "./careerSweep";

const INDEX_LABELS: Record<string, string> = {
  recrutauto_candidates: "Candidats",
  recrutauto_companies: "Entreprises",
  recrutauto_schools: "Ecoles",
  recrutauto_skills: "Competences",
  recrutauto_certifications: "Certifications",
  recrutauto_languages: "Langues",
  recrutauto_interests: "Centres d'interet",
};

const INDEX_KEYS: Record<string, string> = {
  recrutauto_candidates: "candidates",
  recrutauto_companies: "companies",
  recrutauto_schools: "schools",
  recrutauto_skills: "skills",
  recrutauto_certifications: "certifications",
  recrutauto_languages: "languages",
  recrutauto_interests: "interests",
};

const KEY_TO_ES: Record<string, string> = {
  candidates: "recrutauto_candidates",
  companies: "recrutauto_companies",
  schools: "recrutauto_schools",
  skills: "recrutauto_skills",
  certifications: "recrutauto_certifications",
  languages: "recrutauto_languages",
  interests: "recrutauto_interests",
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDuration(start: string, end?: string): string {
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const sec = Math.round((e - s) / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}m ${sec % 60}s`;
}

/**
 * Dérive de volume entre la base et Elasticsearch pour un index.
 * Mêmes seuils que le backend (`es_reconcile_worker._is_count_drift`) : on
 * signale si l'écart dépasse À LA FOIS 5 % (relatif) ET 100 docs (absolu).
 * Pure (aucune IO) — la logique source de vérité est testée côté back.
 */
function computeIndexDrift(
  dbCount: number | null | undefined,
  esCount: number | null | undefined,
): { drifted: boolean; gap: number; relPct: number } | null {
  if (dbCount == null || esCount == null) return null;
  const gap = Math.abs(dbCount - esCount);
  const relPct = dbCount > 0 ? (gap / dbCount) * 100 : gap > 0 ? 100 : 0;
  return { drifted: gap >= 100 && relPct >= 5, gap, relPct };
}

const STATUS_BADGE: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
  pending: { label: "En attente", className: "bg-gray-100 text-gray-700", icon: <Clock className="h-3 w-3" /> },
  running: { label: "En cours", className: "bg-blue-100 text-blue-700", icon: <Loader2 className="h-3 w-3 animate-spin" /> },
  completed: { label: "Termine", className: "bg-green-100 text-green-700", icon: <CheckCircle className="h-3 w-3" /> },
  failed: { label: "Echoue", className: "bg-red-100 text-red-700", icon: <XCircle className="h-3 w-3" /> },
  cancelled: { label: "Annule", className: "bg-orange-100 text-orange-700", icon: <X className="h-3 w-3" /> },
};

export default function AdminOpsPage() {
  const navigate = useNavigate();
  const [esStatus, setEsStatus] = useState<EsStatusResponse | null>(null);
  const [dbStats, setDbStats] = useState<DbStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Reindex jobs tracking
  const [activeJobs, setActiveJobs] = useState<Record<string, ReindexJob>>({});
  const [jobHistory, setJobHistory] = useState<ReindexJob[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Mapping dialog
  const [mappingDialog, setMappingDialog] = useState<{ open: boolean; label: string; data: EsIndexMappingResponse | null; loading: boolean }>({
    open: false, label: "", data: null, loading: false,
  });

  // Services health
  const [servicesHealth, setServicesHealth] = useState<ServicesHealthResponse | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // n8n operations
  const [n8nWorkflows, setN8nWorkflows] = useState<N8nWorkflow[]>([]);
  const [n8nExecutions, setN8nExecutions] = useState<N8nExecution[]>([]);
  const [n8nLoading, setN8nLoading] = useState(false);
  const [n8nFilterWorkflow, setN8nFilterWorkflow] = useState<string>("");
  const [n8nFilterStatus, setN8nFilterStatus] = useState<string>("");

  // S3 backups
  const [s3Backups, setS3Backups] = useState<S3BackupsResponse | null>(null);
  const [s3Loading, setS3Loading] = useState(false);

  // Career metrics sweep (A-player / dominant company type), run by back/
  const [careerSweep, setCareerSweep] = useState<CareerSweepStatus | null>(null);
  const [careerSweepError, setCareerSweepError] = useState<string | null>(null);

  const refreshCareerSweep = useCallback(async () => {
    try {
      setCareerSweep(await getCareerSweepStatus(navigate));
      setCareerSweepError(null);
    } catch (e) {
      setCareerSweepError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  }, [navigate]);

  const careerSweepActive = isCareerSweepActive(careerSweep);

  useEffect(() => {
    refreshCareerSweep();
  }, [refreshCareerSweep]);

  useEffect(() => {
    if (!careerSweepActive) return;
    const id = setInterval(refreshCareerSweep, CAREER_SWEEP_POLL_MS);
    return () => clearInterval(id);
  }, [careerSweepActive, refreshCareerSweep]);

  const handleRestartCareerSweep = async () => {
    const ok = await confirmDialog({
      title: "Relancer le recalcul des parcours",
      description:
        "Toutes les entreprises seront reclassees puis tous les candidats enrichis recalcules (plusieurs heures). Le back reprend a sa prochaine iteration (~1 min).",
      confirmLabel: "Relancer",
    });
    if (!ok) return;
    setActionLoading("career-sweep-restart");
    try {
      await restartCareerSweep(navigate);
      showMessage("success", "Recalcul des parcours relance");
      await refreshCareerSweep();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleShowMapping = async (indexKey: string, label: string) => {
    setMappingDialog({ open: true, label, data: null, loading: true });
    try {
      const data = await getIndexMapping(navigate, indexKey);
      setMappingDialog({ open: true, label, data, loading: false });
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
      setMappingDialog({ open: false, label: "", data: null, loading: false });
    }
  };

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const refreshStatus = useCallback(async () => {
    try {
      const [es, db] = await Promise.all([
        getEsStatus(navigate).catch(() => null),
        getDbStats(navigate).catch(() => null),
      ]);
      setEsStatus(es);
      setDbStats(db);
    } catch { /* ignore */ }
  }, [navigate]);

  const refreshJobs = useCallback(async () => {
    try {
      const jobs = await getReindexJobs(navigate);
      const active: Record<string, ReindexJob> = {};
      const history: ReindexJob[] = [];
      for (const j of jobs) {
        if (j.status === "pending" || j.status === "running") {
          active[j.index_name] = j;
        } else {
          history.push(j);
        }
      }
      setActiveJobs(active);
      setJobHistory(history);
      return Object.keys(active).length > 0;
    } catch {
      return false;
    }
  }, [navigate]);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([refreshStatus(), refreshJobs()]);
      setLoading(false);
    })();
  }, [refreshStatus, refreshJobs]);

  // Polling for active jobs
  useEffect(() => {
    const hasActive = Object.keys(activeJobs).length > 0;

    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const stillActive = await refreshJobs();
        if (!stillActive) {
          // All jobs done, refresh ES status and stop polling
          await refreshStatus();
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      }, 2000);
    }

    if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [activeJobs, refreshJobs, refreshStatus]);

  const handleReindex = async (indexKey: string) => {
    try {
      const res = await startReindex(navigate, indexKey);
      setActiveJobs((prev) => ({
        ...prev,
        [indexKey]: { job_id: res.job_id, index_name: indexKey, status: "pending", progress: 0, total: 0, errors: 0, started_at: new Date().toISOString() },
      }));
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    }
  };

  const handleReindexAll = async () => {
    setActionLoading("reindex-all");
    try {
      const res = await startReindexAll(navigate);
      const newActive: Record<string, ReindexJob> = {};
      for (const [idx, jobId] of Object.entries(res.jobs)) {
        newActive[idx] = { job_id: jobId, index_name: idx, status: "pending", progress: 0, total: 0, errors: 0, started_at: new Date().toISOString() };
      }
      setActiveJobs((prev) => ({ ...prev, ...newActive }));
      if (res.skipped.length > 0) {
        showMessage("error", `Indices ignores (deja en cours): ${res.skipped.join(", ")}`);
      }
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (jobId: string, indexName: string) => {
    try {
      await cancelReindexJob(navigate, jobId);
      setActiveJobs((prev) => {
        const copy = { ...prev };
        if (copy[indexName]) copy[indexName] = { ...copy[indexName], cancel_requested: "1" };
        return copy;
      });
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    }
  };

  const handleDeleteIndex = async (indexKey: string, label: string) => {
    const ok = await confirmDialog({
      title: "Supprimer l'index",
      description: `L'index "${label}" sera supprimé. Les données devront être réindexées.`,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    setActionLoading(`delete-${indexKey}`);
    try {
      await deleteEsIndex(navigate, indexKey);
      showMessage("success", `Index "${label}" supprime`);
      await refreshStatus();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const refreshHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const data = await getServicesHealth(navigate);
      setServicesHealth(data);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setHealthLoading(false);
    }
  }, [navigate]);

  const refreshS3Backups = useCallback(async () => {
    setS3Loading(true);
    try {
      const data = await getS3Backups(navigate);
      setS3Backups(data);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setS3Loading(false);
    }
  }, [navigate]);

  const refreshN8nWorkflows = useCallback(async () => {
    try {
      const { workflows } = await listN8nWorkflows(navigate);
      setN8nWorkflows(workflows);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    }
  }, [navigate]);

  const refreshN8nExecutions = useCallback(async () => {
    try {
      const { executions } = await listN8nExecutions(navigate, {
        workflowId: n8nFilterWorkflow || undefined,
        status: n8nFilterStatus || undefined,
        limit: 50,
      });
      setN8nExecutions(executions);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    }
  }, [navigate, n8nFilterWorkflow, n8nFilterStatus]);

  const refreshN8n = useCallback(async () => {
    setN8nLoading(true);
    try {
      await Promise.all([refreshN8nWorkflows(), refreshN8nExecutions()]);
    } finally {
      setN8nLoading(false);
    }
  }, [refreshN8nWorkflows, refreshN8nExecutions]);

  // Initial load for health + n8n + s3
  useEffect(() => {
    refreshHealth();
    refreshN8n();
    refreshS3Backups();
  }, [refreshHealth, refreshN8n, refreshS3Backups]);

  // Re-run executions query when filters change
  useEffect(() => {
    refreshN8nExecutions();
  }, [n8nFilterWorkflow, n8nFilterStatus, refreshN8nExecutions]);

  const handleToggleWorkflow = async (wf: N8nWorkflow) => {
    setActionLoading(`wf-${wf.id}`);
    try {
      if (wf.active) await deactivateN8nWorkflow(navigate, wf.id);
      else await activateN8nWorkflow(navigate, wf.id);
      showMessage("success", wf.active ? "Workflow desactive" : "Workflow active");
      await refreshN8nWorkflows();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopAllForWorkflow = async (wf: N8nWorkflow) => {
    const ok = await confirmDialog({
      title: "Arrêter toutes les exécutions",
      description: `Toutes les exécutions en cours de "${wf.name}" seront arrêtées.`,
      confirmLabel: "Arrêter",
      destructive: true,
    });
    if (!ok) return;
    setActionLoading(`stopall-${wf.id}`);
    try {
      const res = await stopAllN8nExecutionsForWorkflow(navigate, wf.id);
      showMessage("success", `${res.success.length} execution(s) arretee(s), ${res.failed.length} echec(s)`);
      await refreshN8nExecutions();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleStopExecution = async (execId: string) => {
    setActionLoading(`exec-stop-${execId}`);
    try {
      await stopN8nExecution(navigate, execId);
      showMessage("success", `Execution ${execId} arretee`);
      await refreshN8nExecutions();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteExecution = async (execId: string) => {
    const ok = await confirmDialog({
      title: "Supprimer l'exécution",
      description: `L'exécution ${execId} sera définitivement supprimée.`,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    setActionLoading(`exec-del-${execId}`);
    try {
      await deleteN8nExecution(navigate, execId);
      showMessage("success", `Execution ${execId} supprimee`);
      await refreshN8nExecutions();
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearCache = async () => {
    setActionLoading("cache");
    try {
      const res = await clearCache(navigate);
      showMessage("success", `${res.cleared} cles de cache supprimees`);
    } catch (e) {
      showMessage("error", e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Operations Admin</h1>
          </div>
        </header>

        <div className="p-6 space-y-6 max-w-4xl">
          {message && (
            <div className={`px-4 py-3 rounded text-sm ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
              {message.text}
            </div>
          )}

          {/* Services Healthcheck */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <HeartPulse className="h-4 w-4" /> Services
                </CardTitle>
                <Button variant="outline" size="sm" onClick={refreshHealth} disabled={healthLoading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${healthLoading ? "animate-spin" : ""}`} /> Rafraichir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!servicesHealth ? (
                <p className="text-sm text-muted-foreground">Chargement...</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(servicesHealth.services).map(([name, check]) => (
                    <div key={name} className="border rounded p-3 flex items-start gap-2">
                      {check.status === "ok" ? (
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium capitalize">{name}</div>
                        <div className="text-xs text-muted-foreground">
                          {check.latency_ms} ms
                          {check.detail ? ` — ${check.detail}` : ""}
                        </div>
                        {check.error && (
                          <div className="text-xs text-red-600 mt-1 break-all" title={check.error}>
                            {check.error}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* DB Stats */}
          {dbStats && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Database className="h-4 w-4" /> Base de donnees
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {Object.entries(dbStats).map(([key, count]) => (
                    <div key={key} className="text-center">
                      <div className="text-2xl font-bold">{count.toLocaleString("fr-FR")}</div>
                      <div className="text-xs text-muted-foreground capitalize">{key}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* S3 Backups */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <HardDrive className="h-4 w-4" /> Backups S3
                </CardTitle>
                <Button variant="outline" size="sm" onClick={refreshS3Backups} disabled={s3Loading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${s3Loading ? "animate-spin" : ""}`} /> Rafraichir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {!s3Backups ? (
                <p className="text-sm text-muted-foreground">Chargement...</p>
              ) : !s3Backups.configured ? (
                <p className="text-sm text-muted-foreground">S3 non configure (variables S3_ENDPOINT / S3_ACCESS_KEY manquantes).</p>
              ) : s3Backups.backups.length === 0 ? (
                <div className="flex items-center gap-2 text-orange-600">
                  <AlertTriangle className="h-4 w-4" />
                  <p className="text-sm font-medium">Aucun backup trouve dans le bucket "{s3Backups.bucket}"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{s3Backups.backups.length}</div>
                      <div className="text-xs text-muted-foreground">Base backups</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{s3Backups.wal_count.toLocaleString("fr-FR")}</div>
                      <div className="text-xs text-muted-foreground">Segments WAL</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{formatBytes(s3Backups.total_size_bytes)}</div>
                      <div className="text-xs text-muted-foreground">Taille totale</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {s3Backups.backups[0]?.finish_time
                          ? new Date(s3Backups.backups[0].finish_time).toLocaleDateString("fr-FR")
                          : new Date(s3Backups.backups[0].last_modified).toLocaleDateString("fr-FR")}
                      </div>
                      <div className="text-xs text-muted-foreground">Dernier backup</div>
                    </div>
                  </div>

                  {/* Backup list */}
                  <div className="overflow-auto border rounded">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left">Backup</th>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-right">Taille compresse</th>
                          <th className="px-3 py-2 text-right">Taille brute</th>
                          <th className="px-3 py-2 text-left">Host</th>
                        </tr>
                      </thead>
                      <tbody>
                        {s3Backups.backups.map((b) => (
                          <tr key={b.key} className="border-t">
                            <td className="px-3 py-2 font-mono text-xs">{b.name}</td>
                            <td className="px-3 py-2 text-xs">
                              {b.finish_time
                                ? new Date(b.finish_time).toLocaleString("fr-FR")
                                : new Date(b.last_modified).toLocaleString("fr-FR")}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {b.compressed_size ? formatBytes(b.compressed_size) : "-"}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">
                              {b.uncompressed_size ? formatBytes(b.uncompressed_size) : "-"}
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{b.hostname || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Elasticsearch */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Search className="h-4 w-4" /> Elasticsearch
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { refreshStatus(); refreshJobs(); }} disabled={loading}>
                    <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Rafraichir
                  </Button>
                  <Button size="sm" onClick={handleReindexAll} disabled={!!actionLoading || Object.keys(activeJobs).length > 0}>
                    Tout reindexer
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!esStatus?.enabled ? (
                <p className="text-sm text-muted-foreground">Elasticsearch n'est pas active.</p>
              ) : (
                <div className="overflow-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Index</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-right">Documents (ES)</th>
                        <th className="px-3 py-2 text-right">Base (DB)</th>
                        <th className="px-3 py-2 text-right">Taille</th>
                        <th className="px-3 py-2 text-left min-w-[200px]">Progression</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(esStatus.indices).map(([esName, info]) => {
                        const label = INDEX_LABELS[esName] || esName;
                        const key = INDEX_KEYS[esName] || esName;
                        const job = activeJobs[key];
                        const pct = job && job.total > 0 ? Math.round((job.progress / job.total) * 100) : 0;
                        const dbCount = dbStats ? dbStats[key] : undefined;
                        const drift = computeIndexDrift(dbCount, info.doc_count);

                        return (
                          <tr key={esName} className="border-t">
                            <td className="px-3 py-2 font-medium">
                              <button
                                className="hover:underline hover:text-blue-600 text-left"
                                onClick={() => handleShowMapping(key, label)}
                                title="Voir le mapping"
                              >
                                {label}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              {info.error ? (
                                <span className="text-red-600 text-xs">{info.error}</span>
                              ) : info.exists ? (
                                <span className="text-green-600 text-xs font-medium">Actif</span>
                              ) : (
                                <span className="text-gray-400 text-xs">Absent</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{info.doc_count != null ? info.doc_count.toLocaleString("fr-FR") : "-"}</td>
                            <td className="px-3 py-2 text-right font-mono">
                              {dbCount == null ? (
                                <span className="text-muted-foreground">-</span>
                              ) : drift?.drifted ? (
                                <span
                                  className="inline-flex items-center justify-end gap-1 text-amber-600"
                                  title={`Écart de ${drift.gap.toLocaleString("fr-FR")} docs (${drift.relPct.toFixed(0)} %) entre la base et ES — réindexer cet index`}
                                >
                                  <AlertTriangle className="h-3 w-3" />
                                  {dbCount.toLocaleString("fr-FR")}
                                </span>
                              ) : (
                                dbCount.toLocaleString("fr-FR")
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{info.size_bytes != null ? formatBytes(info.size_bytes) : "-"}</td>
                            <td className="px-3 py-2">
                              {job ? (
                                <div className="flex items-center gap-2">
                                  <Progress value={pct} className="flex-1 h-2" />
                                  <span className="text-xs font-mono w-12 text-right">
                                    {job.status === "pending" ? "..." : `${pct}%`}
                                  </span>
                                  {job.cancel_requested === "1" ? (
                                    <span className="text-xs text-orange-600">Annulation...</span>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => handleCancel(job.job_id, key)}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleReindex(key)}
                                  disabled={!!job || !!actionLoading}
                                  title="Reindexer"
                                >
                                  <RefreshCw className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteIndex(key, label)}
                                  disabled={!!job || !!actionLoading || !info.exists}
                                  title="Supprimer l'index"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Job History */}
          {jobHistory.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Jobs recents</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto border rounded">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left">Index</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-right">Documents</th>
                        <th className="px-3 py-2 text-right">Erreurs</th>
                        <th className="px-3 py-2 text-right">Duree</th>
                        <th className="px-3 py-2 text-left">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobHistory.map((j) => {
                        const badge = STATUS_BADGE[j.status] || STATUS_BADGE.pending;
                        return (
                          <tr key={j.job_id} className="border-t">
                            <td className="px-3 py-2 font-medium">{INDEX_LABELS[KEY_TO_ES[j.index_name] || ""] || j.index_name}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
                                {badge.icon} {badge.label}
                              </span>
                              {j.status === "failed" && j.error_detail && (
                                <p className="text-xs text-red-500 mt-1 max-w-xs truncate" title={j.error_detail}>{j.error_detail}</p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono">{j.progress}/{j.total}</td>
                            <td className="px-3 py-2 text-right font-mono">{j.errors}</td>
                            <td className="px-3 py-2 text-right font-mono">{formatDuration(j.started_at, j.finished_at)}</td>
                            <td className="px-3 py-2 text-xs">{j.started_at ? new Date(j.started_at).toLocaleString("fr-FR") : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Career metrics sweep */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-4 w-4" /> Recalcul des parcours (A-player / type d'entreprise)
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={refreshCareerSweep}>
                    <RefreshCw className="h-3 w-3 mr-1" /> Rafraichir
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate("/company-classification")}>
                    Classification & paliers
                  </Button>
                  <Button size="sm" onClick={handleRestartCareerSweep} disabled={actionLoading === "career-sweep-restart"}>
                    Relancer le recalcul
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {careerSweepError ? (
                <p className="text-sm text-red-600">{careerSweepError}</p>
              ) : !careerSweep ? (
                <p className="text-sm text-muted-foreground">Chargement...</p>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${careerSweepBadge(careerSweep.phase).className}`}>
                      {careerSweepActive && careerSweep.phase && <Loader2 className="h-3 w-3 animate-spin" />}
                      {careerSweepBadge(careerSweep.phase).label}
                    </span>
                    <span className="text-xs text-muted-foreground">version {careerSweep.version ?? "-"}</span>
                  </div>
                  {[
                    { label: "Entreprises", progress: careerSweep.companies_progress, cursor: careerSweep.company_cursor, max: careerSweep.companies_max_id },
                    { label: "Candidats enrichis", progress: careerSweep.candidates_progress, cursor: careerSweep.candidate_cursor, max: careerSweep.processed_candidates_max_id },
                  ].map((row) => (
                    <div key={row.label} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="font-medium">{row.label}</span>
                        <span className="font-mono text-muted-foreground">
                          {toPercent(row.progress)} % (id {row.cursor} / {row.max})
                        </span>
                      </div>
                      <Progress value={toPercent(row.progress)} className="h-2" />
                    </div>
                  ))}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Candidats calcules</span><span className="font-mono">{formatCoverage(careerSweep.coverage.with_career_metrics, careerSweep.coverage.processed_candidates)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Score de progression</span><span className="font-mono">{formatCoverage(careerSweep.coverage.with_progression_score, careerSweep.coverage.processed_candidates)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Type d'entreprise dominant</span><span className="font-mono">{formatCoverage(careerSweep.coverage.with_dominant_company_type, careerSweep.coverage.processed_candidates)}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Entreprises classees</span><span className="font-mono">{formatCoverage(careerSweep.coverage.classified_companies, careerSweep.coverage.companies)}</span></div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Enrichissement des entreprises sans secteur :{" "}
                    {careerSweep.company_enrichment_done ? "passe terminee" : `en cours (id ${careerSweep.enrich_cursor})`}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* n8n Operations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Workflow className="h-4 w-4" /> n8n
                </CardTitle>
                <Button variant="outline" size="sm" onClick={refreshN8n} disabled={n8nLoading}>
                  <RefreshCw className={`h-3 w-3 mr-1 ${n8nLoading ? "animate-spin" : ""}`} /> Rafraichir
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-2">Workflows ({n8nWorkflows.length})</h3>
                <div className="overflow-auto border rounded max-h-80">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Nom</th>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-left">Mis a jour</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {n8nWorkflows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground text-xs">
                            Aucun workflow
                          </td>
                        </tr>
                      ) : (
                        n8nWorkflows.map((wf) => (
                          <tr key={wf.id} className="border-t">
                            <td className="px-3 py-2 font-medium">
                              <button
                                className="hover:underline hover:text-blue-600 text-left"
                                onClick={() =>
                                  setN8nFilterWorkflow(n8nFilterWorkflow === wf.id ? "" : wf.id)
                                }
                                title="Filtrer les executions"
                              >
                                {wf.name}
                              </button>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{wf.id}</td>
                            <td className="px-3 py-2">
                              {wf.active ? (
                                <span className="text-green-600 text-xs font-medium">Actif</span>
                              ) : (
                                <span className="text-gray-400 text-xs">Inactif</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {wf.updatedAt ? new Date(wf.updatedAt).toLocaleString("fr-FR") : "-"}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleToggleWorkflow(wf)}
                                  disabled={actionLoading === `wf-${wf.id}`}
                                  title={wf.active ? "Desactiver" : "Activer"}
                                >
                                  {wf.active ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleStopAllForWorkflow(wf)}
                                  disabled={actionLoading === `stopall-${wf.id}`}
                                  title="Arreter toutes les executions en cours"
                                >
                                  <StopCircle className="h-3 w-3" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <h3 className="text-sm font-semibold">Executions ({n8nExecutions.length})</h3>
                  <div className="flex gap-2 items-center text-xs">
                    {n8nFilterWorkflow && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        Workflow: {n8nFilterWorkflow}
                        <button onClick={() => setN8nFilterWorkflow("")} className="hover:text-blue-900">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    <select
                      value={n8nFilterStatus}
                      onChange={(e) => setN8nFilterStatus(e.target.value)}
                      className="border rounded px-2 py-1 text-xs"
                    >
                      <option value="">Tous statuts</option>
                      <option value="running">En cours</option>
                      <option value="waiting">En attente</option>
                      <option value="success">Succes</option>
                      <option value="error">Erreur</option>
                      <option value="canceled">Annulee</option>
                      <option value="crashed">Crash</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-auto border rounded max-h-96">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">ID</th>
                        <th className="px-3 py-2 text-left">Workflow</th>
                        <th className="px-3 py-2 text-left">Statut</th>
                        <th className="px-3 py-2 text-left">Mode</th>
                        <th className="px-3 py-2 text-left">Debut</th>
                        <th className="px-3 py-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {n8nExecutions.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-3 py-4 text-center text-muted-foreground text-xs">
                            Aucune execution
                          </td>
                        </tr>
                      ) : (
                        n8nExecutions.map((ex) => {
                          const running = ex.status === "running" || ex.status === "waiting";
                          return (
                            <tr key={ex.id} className="border-t">
                              <td className="px-3 py-2 font-mono text-xs">{ex.id}</td>
                              <td className="px-3 py-2 font-mono text-xs">{ex.workflowId}</td>
                              <td className="px-3 py-2 text-xs">{ex.status ?? "-"}</td>
                              <td className="px-3 py-2 text-xs">{ex.mode ?? "-"}</td>
                              <td className="px-3 py-2 text-xs">
                                {ex.startedAt ? new Date(ex.startedAt).toLocaleString("fr-FR") : "-"}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="flex gap-1 justify-end">
                                  {running && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleStopExecution(ex.id)}
                                      disabled={actionLoading === `exec-stop-${ex.id}`}
                                      title="Arreter l'execution"
                                    >
                                      <StopCircle className="h-3 w-3" />
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeleteExecution(ex.id)}
                                    disabled={actionLoading === `exec-del-${ex.id}`}
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cache */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Zap className="h-4 w-4" /> Cache Redis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Vider le cache des tokens d'authentification</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearCache}
                  disabled={!!actionLoading}
                >
                  {actionLoading === "cache" ? "Vidage..." : "Vider le cache"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Mapping Dialog */}
        <Dialog open={mappingDialog.open} onOpenChange={(open) => !open && setMappingDialog((prev) => ({ ...prev, open: false }))}>
          <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Mapping — {mappingDialog.label}</DialogTitle>
            </DialogHeader>
            {mappingDialog.loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : mappingDialog.data ? (
              <div className="overflow-auto flex-1">
                <h3 className="text-sm font-semibold mb-2">Mappings</h3>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[50vh] whitespace-pre-wrap">
                  {JSON.stringify(mappingDialog.data.mappings, null, 2)}
                </pre>
                <h3 className="text-sm font-semibold mt-4 mb-2">Settings</h3>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-auto max-h-[30vh] whitespace-pre-wrap">
                  {JSON.stringify(mappingDialog.data.settings, null, 2)}
                </pre>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
